import { PlusSquareIcon, TrashIcon } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  Autocomplete,
  GenericTable,
  ItemStatusEnum,
  Spinner,
  useApiQuery,
} from '@/modules/common';
import { Mask } from '@/modules/masks/types';
import { OverlayItem } from '@/modules/overlays/types';

interface MasksSelectionProps {
  defaultValue: { maskId: string; overlayId: string }[];
}

interface Column extends Mask {
  overlay: string;
  overlayImageUrl: string;
}

export function MaskSelection({ defaultValue }: MasksSelectionProps) {
  const { control, watch } = useFormContext();
  const { isFetching: isMasksFetching, data: masks } = useApiQuery<Mask[]>(
    '/masks',
    {
      queryKey: ['masks'],
      initialData: [],
    }
  );
  const { data: overlays } = useApiQuery<OverlayItem[]>('/overlays', {
    queryKey: ['overlays'],
    initialData: [],
  });

  const filteredMasksData = useMemo(
    () => masks.filter((item) => item.status === ItemStatusEnum.Ready),
    [masks]
  );

  const filteredOverlaysData = useMemo(
    () => overlays.filter((item) => item.status === ItemStatusEnum.Ready),
    [overlays]
  );

  const formattedMasksData = useMemo((): Record<string, Mask> => {
    if (!masks) return {};

    return mapValues(keyBy(masks, 'id'));
  }, [masks]);

  const formattedOverlayData = useMemo((): Record<string, OverlayItem> => {
    if (!overlays) return {};

    return mapValues(keyBy(overlays, 'id'));
  }, [overlays]);

  const fields = watch('masks', defaultValue);
  const { append, remove } = useFieldArray({
    control,
    name: 'masks',
  });

  const filledMasksId = fields.map((style) => style.maskId).filter(Boolean);

  const maskOptions = filteredMasksData
    ?.filter((item) => !filledMasksId.includes(item.id))
    ?.map((style) => ({
      value: style.name,
      id: style.id,
    }));

  const overlaysOptions = filteredOverlaysData?.map((item) => ({
    value: item.name,
    id: item.id,
  }));

  const columns: ColumnDef<Column>[] = [
    {
      size: 200,
      accessorKey: 'name',
      header: 'Mask Name',
      cell: ({ row }) => (
        <Autocomplete
          name={`masks.${row.index}.maskId`}
          control={control}
          searchable
          placeholder="Select mask"
          options={maskOptions}
          className="w-[200px] border-0"
          defaultValue={row.getValue('name')}
        />
      ),
    },
    {
      accessorKey: 'imagesPublicUrls',
      header: 'Preview',
      cell: ({ row }) => {
        const images = Object.values({
          large: row.original.imagesPublicUrls?.large,
          medium: row.original.imagesPublicUrls?.transparent,
        }).filter(Boolean);

        return (
          <div className="flex gap-2">
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt="Mask preview image"
                className="w-[30px] h-[34px] rounded-lg"
              />
            ))}
          </div>
        );
      },
    },
    {
      size: 200,
      accessorKey: 'overlay',
      header: 'Overlay name',
      cell: ({ row }) => (
        <Autocomplete
          name={`masks.${row.index}.overlayId`}
          control={control}
          searchable
          placeholder="Select overlay"
          options={overlaysOptions}
          className="w-[200px] border-0"
          defaultValue={row.getValue('overlay')}
        />
      ),
    },
    {
      accessorKey: 'overlayImageUrl',
      header: 'Overlay Preview',
      cell: ({ row }) => {
        return row.original.overlayImageUrl ? (
          <img
            src={row.original.overlayImageUrl}
            alt="Overlay preview image"
            className="w-[34px] h-[34px] rounded-lg object-cover"
          />
        ) : null;
      },
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          data-testid="remove-mask-button"
          variant="ghost"
          size="icon"
          onClick={() => remove(row.index)}
        >
          <TrashIcon size={28} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];

  const addNewEmptyMask = () => {
    append({
      maskId: '',
      overlayId: '',
    });
  };

  const formattedMasks = fields.map((item) => {
    const formattedMask = formattedMasksData[item.maskId];
    const formattedOverlay = formattedOverlayData[item.overlayId];

    return {
      ...item,
      id: formattedMask?.id ?? '',
      name: formattedMask?.name ?? '',
      imagesPublicUrls: formattedMask?.imagesPublicUrls,
      overlay: formattedOverlay?.name,
      overlayImageUrl: formattedOverlay?.imagePublicUrl,
    };
  });

  return (
    <div>
      {isMasksFetching ? (
        <Spinner />
      ) : (
        <>
          <GenericTable
            name="masks"
            columns={columns}
            key="masks-table"
            data={formattedMasks}
            emptyCellClassName="h-20"
            tableCellClassName="py-1"
            tableRowClassName="hover:bg-transparent"
            emptyDescription="No masks added. Please add new mask."
          />
          <Button
            disabled={isMasksFetching}
            variant="transparent"
            data-testid="add-mask-button"
            className="mt-4"
            onClick={addNewEmptyMask}
          >
            <PlusSquareIcon />
            Add mask
          </Button>
        </>
      )}
    </div>
  );
}
